import React from "react";
import MatchTime from "@/components/KbvMatchTime";
import MatchCards from "@/components/KbvMatchCards";
import KbvOddsStatus from "@/components/KbvOddsStatus";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { getOddValue, colorOddsChanges } from "@/utils/matchInfo";

import {
  LIVE_STATUSES,
  FINISHED_STATUSES,
  NOT_STARTED_STATUS,
} from "@/contants";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useMatchOdds } from "@/hooks/useMatchOdds";

export default function Match({ match }) {
  const { getScore, getOdds } = useLiveDataForMatch(match.id);
  const liveScore = getScore();
  const liveOdds = getOdds();
  // Filter odds với companyId = -1 (tất cả)
  const filterOdds = useFilterOdds(match, liveOdds, null, -1);
  // Lấy tất cả odds từ useMatchOdds
  const odds = useMatchOdds(match, filterOdds, -1);

  const handicapValue = odds.handicapValue;
  const homeOdds = odds.homeHandicapOdds;
  const awayOdds = odds.awayHandicapOdds;
  const homeTrend = odds.homeHandicapTrend;
  const awayTrend = odds.awayHandicapTrend;
  const isAwayHandicap = parseFloat(handicapValue) < 0;

  // Over/Under odds
  const overUnderValue = odds.overUnderValue;
  const overOdds = odds.overOdds;
  const underOdds = odds.underOdds;
  const overTrend = odds.overTrend;
  const underTrend = odds.underTrend;

  // Europe odds (1X2)
  const homeEuropeOdds = odds.homeEuropeOdds;
  const awayEuropeOdds = odds.awayEuropeOdds;
  const instantDrawOdds = odds.instantDrawOdds;
  const homeEuropeTrend = odds.homeEuropeTrend;
  const awayEuropeTrend = odds.awayEuropeTrend;
  const drawTrend = odds.drawTrend;

  const statusNum =
    liveScore?.status !== undefined
      ? Number(liveScore.status)
      : Number(match.status);
  const isLive = LIVE_STATUSES.includes(statusNum);
  const isFinished = FINISHED_STATUSES.includes(statusNum);
  const isNotStarted = NOT_STARTED_STATUS.includes(statusNum);

  const {
    currentHomeScore,
    currentAwayScore,
    currentHomeHalfScore,
    currentAwayHalfScore,
    isHomeWin,
    isAwayWin,
    homeYellow,
    homeRed,
    awayYellow,
    awayRed,
    currentHomeCorner,
    currentAwayCorner,
  } = useMatchData(liveScore, match);

  return (
    <div className="border border-[var(--theme-border)] rounded-lg p-2 flex items-center gap-1 md:gap-3 bg-[var(--theme-foreground)]">
      <div className="flex md:flex-row flex-col items-center gap-1 min-w-fit">
        <MatchTime
          homeCorner={currentHomeCorner}
          awayCorner={currentAwayCorner}
          isLive={isLive}
          isFinished={isFinished}
          isNotStarted={isNotStarted}
          matchTime={match.matchTime}
          liveScore={liveScore}
          status={statusNum}
          halfStartTime={match.halfStartTime}
          isHomeWin={isHomeWin}
          isAwayWin={isAwayWin}
          currentAwayScore={currentAwayScore}
          currentHomeScore={currentHomeScore}
        />
      </div>

      <div className="flex-1 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-1">
            {match.homeTeam?.logo && (
              <img
                src={match.homeTeam?.logo}
                alt={match.homeName}
                width={16}
                height={16}
                className="rounded-full md:w-[20px] md:h-[20px] w-[16px] h-[16px]"
              />
            )}
            <div className="flex gap-1 truncate lg:overflow-visible">
              <span
                className={`text-[11px] sm:text-xs md:text-sm flex-1 min-w-0 ${isHomeWin ? "font-bold" : ""} truncate max-w-[55px] md:max-w-[170px] lg:max-w-none lg:overflow-visible lg:whitespace-normal lg:min-w-0`}
              >
                {match.homeName}
              </span>
              <MatchCards yellow={homeYellow} red={homeRed} direction="home" />
            </div>
          </div>
          {(isFinished || (!isNotStarted)) && (
            <span
              className={`text-[11px] ${isFinished ? '' : 'md:block hidden'} sm:text-xs md:text-sm min-w-[10px] tabular-nums text-center ${
                isHomeWin ? "font-bold" : ""
              }`}
            >
              {currentHomeScore}
              <span className="ml-1 text-[11px] sm:text-xs md:text-sm text-[var(--text-half-score)] font-normal">
                ({currentHomeHalfScore})
              </span>
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-1">
            {match.awayTeam?.logo && (
              <img
                src={match.awayTeam?.logo}
                alt={match.awayName}
                width={16}
                height={16}
                className="rounded-full md:w-[20px] md:h-[20px] w-[16px] h-[16px]"
              />
            )}
            <div className="flex gap-1 truncate lg:overflow-visible">
              <span
                className={`text-[11px] sm:text-xs md:text-sm flex-1 min-w-0 ${isAwayWin ? "font-bold" : ""} truncate max-w-[55px] md:max-w-[170px] lg:max-w-none lg:overflow-visible lg:whitespace-normal lg:min-w-0`}
              >
                {match.awayName}
              </span>
              <MatchCards yellow={awayYellow} red={awayRed} direction="away" />
            </div>
          </div>
          {(isFinished || (!isNotStarted)) && (
            <span
              className={`text-[11px] ${isFinished ? '' : 'md:block hidden'} sm:text-xs md:text-sm min-w-[10px] tabular-nums text-center ${
                isAwayWin ? "font-bold" : ""
              }`}
            >
              {currentAwayScore}
              <span className="ml-1 text-[11px] sm:text-xs md:text-sm text-[var(--text-half-score)] font-normal">
                ({currentAwayHalfScore})
              </span>
            </span>
          )}
        </div>
      </div>

      {!isFinished && (
        <div className="flex flex-row items-stretch md:justify-end gap-2.5 md:gap-4 w-fit">
          {/* Kèo chấp */}
          <div className="flex flex-col gap-2 justify-center w-[65px] md:w-[100px]">
            <div className="flex flex-1 items-end md:gap-4 gap-1 justify-between">
              {!isAwayHandicap && (
                <span className="text-[10px] flex-1 sm:text-xs md:text-sm text-center text-[var(--theme-highlight)] min-w-[18px]">{`${
                  parseFloat(handicapValue) > 0
                    ? `-${handicapValue}`
                    : handicapValue
                }`}</span>
              )}
              <div className="flex flex-1 justify-between items-center gap-1 md:gap-2 min-w-[32px] md:min-w-[45px]">
                <KbvOddsStatus trend={homeTrend} />
                <span
                  className={`font-semibold font-semibold text-[10px] sm:text-xs md:text-sm ${colorOddsChanges(homeTrend)}`}
                >
                  {homeOdds}
                </span>
              </div>
            </div>
            <div className="flex flex-1 items-start md:gap-4 gap-1 justify-end">
              {!isAwayHandicap && (
                <span className="text-[10px] flex-1 sm:text-xs md:text-sm text-center text-[var(--theme-highlight)] min-w-[18px] opacity-0">-</span>
              )}
              <div className="flex flex-1 justify-between items-center gap-1 md:gap-2 min-w-[32px] md:min-w-[45px]">
                <KbvOddsStatus trend={awayTrend} />
                <span
                  className={`font-semibold font-semibold text-[10px] sm:text-xs md:text-sm ${colorOddsChanges(awayTrend)}`}
                >
                  {awayOdds}
                </span>
              </div>
            </div>
          </div>
          {/* Tài/Xỉu odds */}
          <div className="flex flex-col gap-2 justify-start px-1 md:px-2 w-[60px] md:w-[120px]">
            <div className="flex flex-1 items-end md:gap-4 gap-1 justify-between">
              {!isAwayHandicap && (
                <span className="text-[10px] flex-1 text-center sm:text-xs md:text-sm text-[var(--theme-highlight)] min-w-[18px]">{overUnderValue}</span>
              )}
              <div className="flex flex-1 justify-between items-center gap-1 md:gap-2 min-w-[32px] md:min-w-[45px]">
                <KbvOddsStatus trend={overTrend} />
                <span
                  className={`font-semibold font-semibold text-[10px] sm:text-xs md:text-sm ${colorOddsChanges(overTrend)}`}
                >
                  {overOdds}
                </span>
              </div>
            </div>
            <div className="flex flex-1 items-start md:gap-4 gap-1 justify-end">
              <span className="text-[10px] flex-1 text-center sm:text-xs md:text-sm text-[var(--theme-highlight)] min-w-[18px]">
                U
              </span>

              <div className="flex flex-1 justify-between items-center gap-1 md:gap-2 min-w-[32px] md:min-w-[45px]">
                <KbvOddsStatus trend={underTrend} />
                <span
                  className={`font-semibold font-semibold text-[10px] sm:text-xs md:text-sm ${colorOddsChanges(underTrend)}`}
                >
                  {underOdds}
                </span>
              </div>
            </div>
          </div>

          {/* Kèo châu Âu odds */}
          <div className="flex flex-col gap-2 w-[40px] sm:w-[56px] px-[2px] sm:px-1">
            <div className="flex items-center md:gap-2 flex-1 justify-end min-w-[32px] md:min-w-[45px]">
              <KbvOddsStatus trend={homeEuropeTrend} />
              <span
                className={`font-semibold font-semibold text-[10px] sm:text-xs md:text-sm ${colorOddsChanges(homeEuropeTrend)}`}
              >
                {homeEuropeOdds}
              </span>
            </div>
            <div className="flex items-center md:gap-2 flex-1 justify-end min-w-[32px] md:min-w-[45px]">
              <KbvOddsStatus trend={drawTrend} />
              <span
                className={`font-semibold font-semibold text-[10px] sm:text-xs md:text-sm ${colorOddsChanges(drawTrend)}`}
              >
                {instantDrawOdds}
              </span>
            </div>
            <div className="flex items-center md:gap-2 flex-1 justify-end min-w-[32px] md:min-w-[45px]">
              <KbvOddsStatus trend={awayEuropeTrend} />
              <span
                className={`font-semibold font-semibold text-[10px] sm:text-xs md:text-sm ${colorOddsChanges(awayEuropeTrend)}`}
              >
                {awayEuropeOdds}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
